import React, { useCallback, useEffect, useRef, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import AceEditor from 'react-ace'
import 'ace-builds/src-noconflict/mode-javascript'
import 'ace-builds/src-noconflict/theme-xcode'
import 'ace-builds/src-noconflict/ext-searchbox'
import { useSmartEndpointForm } from '../SmartEndpointFormContext'
import { validateSource } from './utils/smartEndpointValidation'
import { formatSource } from './utils/formatSource'
import { registerEditorCommands } from './utils/editorCommands'

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden'
  },
  editorWrapper: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden'
  },
  statusBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--gap-md)',
    padding: '3px 10px',
    borderTop: '1px solid var(--color-neutral-200)',
    backgroundColor: 'var(--color-neutral-050)',
    fontSize: '11px',
    color: 'var(--color-neutral-500)',
    flexShrink: 0,
    userSelect: 'none'
  },
  statusSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  errorIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
    cursor: 'pointer',
    padding: '1px 6px',
    borderRadius: 'var(--border-radius-sm)',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-200)'
    }
  },
  errorDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    flexShrink: 0
  },
  formatBtn: {
    padding: '1px 8px',
    border: 'none',
    borderRadius: 'var(--border-radius-sm)',
    backgroundColor: 'transparent',
    color: 'var(--color-neutral-500)',
    fontSize: '11px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-200)',
      color: 'var(--color-neutral-700)'
    }
  },
  spacer: {
    flex: 1
  }
}))

function SourceEditor () {
  const classes = useStyles()
  const form = useSmartEndpointForm()
  const editorRef = useRef(null)
  const validationTimeoutRef = useRef(null)
  const currentValueRef = useRef(form.source || '')

  const [diagnostics, setDiagnostics] = useState([])
  const [cursorPos, setCursorPos] = useState({ row: 1, col: 1 })

  useEffect(() => {
    currentValueRef.current = form.source || ''
  }, [form.source])

  const updateDiagnostics = useCallback((newDiagnostics) => {
    setDiagnostics(newDiagnostics)
    const editor = editorRef.current?.editor
    if (editor) {
      editor.getSession().setAnnotations(newDiagnostics)
    }
  }, [])

  const scheduleValidation = useCallback((value) => {
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current)
    }
    validationTimeoutRef.current = setTimeout(() => {
      updateDiagnostics(validateSource(value))
    }, 250)
  }, [updateDiagnostics])

  const handleFormat = useCallback(async () => {
    const editor = editorRef.current?.editor
    const value = currentValueRef.current || ''
    if (!editor || !value.trim()) return

    try {
      const formatted = await formatSource(value)
      if (formatted === value) return

      const cursor = editor.getCursorPosition()
      editor.setValue(formatted, -1)
      editor.clearSelection()
      currentValueRef.current = formatted

      const maxRow = Math.max(editor.session.getLength() - 1, 0)
      const row = Math.min(cursor.row, maxRow)
      const line = editor.session.getLine(row) || ''
      editor.moveCursorTo(row, Math.min(cursor.column, line.length))

      form.setSource(formatted)
      scheduleValidation(formatted)
    } catch (error) {
      updateDiagnostics([{ row: 0, column: 0, type: 'warning', text: `Format failed: ${error.message}` }])
    }
  }, [form, scheduleValidation, updateDiagnostics])

  const handleEditorLoad = useCallback((editor) => {
    registerEditorCommands(editor, {
      onFormat: handleFormat
    })

    editor.selection.on('changeCursor', () => {
      const pos = editor.getCursorPosition()
      setCursorPos({ row: pos.row + 1, col: pos.column + 1 })
    })

    updateDiagnostics(validateSource(currentValueRef.current))
  }, [handleFormat, updateDiagnostics])

  const handleChange = useCallback((value) => {
    currentValueRef.current = value
    form.setSource(value)
    scheduleValidation(value)
  }, [form, scheduleValidation])

  const handleJumpToError = useCallback(() => {
    const editor = editorRef.current?.editor
    if (!editor || diagnostics.length === 0) return
    const first = diagnostics[0]
    editor.gotoLine(first.row + 1, first.column, true)
    editor.focus()
  }, [diagnostics])

  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current)
      }
    }
  }, [])

  const errorCount = diagnostics.filter(d => d.type === 'error').length
  const warningCount = diagnostics.filter(d => d.type === 'warning').length

  return (
    <div className={classes.container}>
      <div className={classes.editorWrapper}>
        <AceEditor
          ref={editorRef}
          height='100%'
          width='100%'
          mode='javascript'
          theme='xcode'
          name='source-editor'
          editorProps={{ $blockScrolling: true }}
          value={form.source}
          onLoad={handleEditorLoad}
          onChange={handleChange}
          tabSize={2}
          fontSize={13}
          showPrintMargin={false}
          showGutter
          highlightActiveLine
          setOptions={{
            useWorker: false,
            showLineNumbers: true,
            wrap: true,
            enableMultiselect: true
          }}
          placeholder='// Write your smart endpoint source here...'
        />
      </div>

      <div className={classes.statusBar}>
        <div
          className={classes.errorIndicator}
          onClick={handleJumpToError}
          title={errorCount > 0 ? 'Jump to first error' : 'No errors'}
        >
          <span
            className={classes.errorDot}
            style={{
              backgroundColor: errorCount > 0
                ? 'var(--color-danger-500)'
                : warningCount > 0
                  ? 'var(--color-warning-500)'
                  : 'var(--color-success-500)'
            }}
          />
          {errorCount > 0 && <span style={{ color: 'var(--color-danger-600)' }}>{errorCount} error{errorCount !== 1 ? 's' : ''}</span>}
          {errorCount === 0 && warningCount > 0 && <span>{warningCount} warning{warningCount !== 1 ? 's' : ''}</span>}
          {errorCount === 0 && warningCount === 0 && <span>No issues</span>}
        </div>

        <span className={classes.statusSection}>
          Ln {cursorPos.row}, Col {cursorPos.col}
        </span>

        <span className={classes.spacer} />

        <button
          className={classes.formatBtn}
          onClick={handleFormat}
          title='Format (Shift+Alt+F)'
        >
          Format
        </button>

        <span style={{ color: 'var(--color-neutral-400)' }}>JavaScript</span>
      </div>
    </div>
  )
}

export default SourceEditor
